import type { IntegrationSpec } from '../../interfaces/integrationSpec';
import { IntegrationCategory } from '../../interfaces/integrationSpec';
import {
	isDashboardIntegration,
	isDataformIntegration,
	isDBTCloudIntegration,
	isSchemaFirstIntegration,
} from './integrations.utils';

export enum IntegrationPreferenceType {
	METADATA_MANAGEMENT = 'metadata_management',
	RESOURCE_MANAGEMENT = 'resource_management',
	METADATA_PUSH = 'metadata_push',
}

export type PreferenceOption = {
	credentialFieldName?: string;
	integrationFieldName?: string;
	title: string;
	description: string;
	type: IntegrationPreferenceType;
	defaultValue?: boolean;
};

export type IntegrationTab = {
	name: string;
	// Name of the tab displayed on the left sidebar, if not provided, name will be used
	displayName?: string;
	title?: string;
	description?: string;
	options?: PreferenceOption[];
	preserveCase?: boolean;
	hidden?: boolean;
};

const PREFERENCE_AUTO_STALE: PreferenceOption = {
	credentialFieldName: 'disable_auto_stale',
	title: 'Prevent automatic staling',
	description:
		'Preserve resources that are missing from subsequent extractions',
	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
};

const PREFERENCE_DISABLE_USAGE_EXTRACTION: PreferenceOption = {
	credentialFieldName: 'disable_extract_usage',
	title: 'Disable reading query history',
	description:
		'Disable reading and syncing query, lineage and usage metadata for the integration',
	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
};

const PREFERENCE_DISABLE_SYNC_ALL_DATABASES: PreferenceOption = {
	credentialFieldName: 'disable_sync_all_databases',
	title: 'Disable syncing all databases',
	description:
		'Disable syncing all databases in the integration and only sync the provided database',
	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
};

// There is no backend-end reference to this credential field...
// Commented on: 2025-08-13
// const PREFERENCE_DISABLE_AUTO_EXTRACT_NEW_SCHEMAS: PreferenceOption = {
// 	credentialFieldName: 'disable_auto_extract_new_schemas',
// 	title: 'Disable automatic schema extraction',
// 	description:
// 		'By default, Secoda will automatically extract new schemas that are added to your integration. Check this option to disable this behavior',
// 	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
// };

const PREFERENCE_EXCLUDE_PERSONAL_COLLECTIONS: PreferenceOption = {
	credentialFieldName: 'exclude_personal_collections',
	title: 'Disable personal groups',
	description: 'Exclude resources from personal groups',
	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
};

const PREFERENCE_ENABLE_EXTRACT_UNPUBLISHED_RESOURCES: PreferenceOption = {
	credentialFieldName: 'enable_extract_unpublished_resources',
	title: 'Unpublished resources',
	description: 'Enable Secoda to extract resources that are not yet published',
	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
};

const PREFERENCE_ENABLE_FETCHING_JOBS_FROM_30_DAYS: PreferenceOption = {
	credentialFieldName: 'enable_fetching_jobs_from_30_days',
	title: 'Fetch jobs from 30 days',
	description: 'Enable Secoda to fetch jobs from the last 30 days for lineage',
	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
};

const PREFERENCE_DISABLE_NEW_SCHEMA_TO_VISIBLE: PreferenceOption = {
	credentialFieldName: 'disable_new_schema_and_group_resources_extraction',
	title: 'Disable resources from new databases/schemas from being extracted',
	description:
		'Resources belonging to new databases and/or schemas will not be extracted',
	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
};

const PREFERENCE_DISABLE_NEW_GROUPS_TO_VISIBLE: PreferenceOption = {
	credentialFieldName: 'disable_new_schema_and_group_resources_extraction',
	title: 'Disable resources from new dashboard groups from being extracted',
	description:
		'Resources belonging to new dashboard groups will not be extracted',
	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
};

const PREFERENCE_PUSH_SYNC_DBT_COLUMNS: PreferenceOption = {
	credentialFieldName: 'push_update_dbt_columns',
	title: 'Sync existing columns to DBT models',
	description:
		'For each table created from a DBT model, update the descriptions of columns already defined in the model using the values from Secoda.',
	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
	defaultValue: true, // Must match the default value in api/integration/connections/github/github.py
};

const PREFERENCE_PUSH_SYNC_NEW_DBT_COLUMNS_WITH_DESCRIPTIONS: PreferenceOption =
	{
		credentialFieldName: 'push_sync_missing_columns_with_descriptions_to_dbt',
		title: 'Sync missing columns with descriptions to DBT models',
		description:
			"For each table created from a DBT model, add the columns that aren't already defined in the model and have descriptions available in Secoda",
		type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
		defaultValue: true, // Must match the default value in api/integration/connections/github/github.py
	};

const PREFERENCE_PUSH_SYNC_NEW_DBT_COLUMNS_WITHOUT_DESCRIPTIONS: PreferenceOption =
	{
		credentialFieldName:
			'push_sync_missing_columns_without_descriptions_to_dbt',
		title: 'Sync missing columns without descriptions to DBT models',
		description:
			"For each table created from a DBT model, add the columns that aren't already defined in the model but do not have descriptions set in Secoda",
		type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
		defaultValue: true, // Must match the default value in api/integration/connections/github/github.py
	};

export const PREFERENCE_FORCE_SOURCE_TAGS: PreferenceOption = {
	integrationFieldName: 'force_source_tags',
	title: 'Force source tags',
	description:
		'Always use tags from the source, overwriting any changes made in Secoda',
	type: IntegrationPreferenceType.METADATA_MANAGEMENT,
};

export const PREFERENCE_FORCE_SOURCE_OWNERS: PreferenceOption = {
	integrationFieldName: 'force_source_owners',
	title: 'Force source owners',
	description:
		'Always use owners from the source, overwriting any changes made in Secoda',
	type: IntegrationPreferenceType.METADATA_MANAGEMENT,
};

export const PREFERENCE_FORCE_SOURCE_DESCRIPTION: PreferenceOption = {
	integrationFieldName: 'force_source_description',
	title: 'Force source description',
	description:
		'Always use descriptions from the source, overwriting any changes made in Secoda',
	type: IntegrationPreferenceType.METADATA_MANAGEMENT,
};

export const PREFERENCE_PUSH_TAGS_TO_SOURCE: PreferenceOption = {
	integrationFieldName: 'push_tags_to_source',
	title: 'Push tags to source',
	description:
		'Push tags created or modified in Secoda back to the source system',
	type: IntegrationPreferenceType.METADATA_PUSH,
};

export const PREFERENCE_PUSH_OWNERS_TO_SOURCE: PreferenceOption = {
	integrationFieldName: 'push_owners_to_source',
	title: 'Push owners to source',
	description: 'Push owners assigned in Secoda back to the source system',
	type: IntegrationPreferenceType.METADATA_PUSH,
};

export const PREFERENCE_PUSH_DESCRIPTION_TO_SOURCE: PreferenceOption = {
	integrationFieldName: 'push_description_to_source',
	title: 'Push description to source',
	description:
		'Push descriptions created or modified in Secoda back to the source system',
	type: IntegrationPreferenceType.METADATA_PUSH,
};

export const PREFERENCE_CONFLUENCE_IMPORT_KNOWLEDGE_BASES: PreferenceOption = {
	credentialFieldName: 'confluence_import_knowledge_bases',
	title: 'Import knowledge base spaces',
	description:
		'By default only "Collaboration" spaces are imported. Enable this to also import "Knowledge base" spaces',
	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
};

const PREFERENCE_RESTRICT_TESTS_BY_TEAMS: PreferenceOption = {
	credentialFieldName: 'restrict_tests_by_teams',
	title: 'Restrict test visibility by teams',
	description:
		'When enabled, only users who are members of teams assigned to this integration will be able to see tests from this integration',
	type: IntegrationPreferenceType.RESOURCE_MANAGEMENT,
};

interface SupportedTabsOptions {
	isAIEnabledWorkspace: boolean;
	isExtensionReadOnly?: boolean;
	secodaCoreAccess?: boolean;
}

export function getSupportedTabs(
	spec: IntegrationSpec,
	options: SupportedTabsOptions
): IntegrationTab[] {
	let tabs: IntegrationTab[] = [];

	const isDatabaseOrWarehouse =
		spec.type === 'builtin' &&
		spec.value.categories.find(
			(cat) =>
				cat === IntegrationCategory.WAREHOUSE ||
				cat === IntegrationCategory.DATABASE
		);

	// Check if this integration has disablePullMetadata set to true
	const supportsPushOrPullMetadata =
		spec.type === 'marketplace' ||
		(spec.type === 'builtin' &&
			(spec.value.supportsPushMetadata || !spec.value.disablePullMetadata));

	const connection = { name: 'connection' };
	const syncs = {
		name: 'syncs',
		displayName: 'Syncs',
		title: 'Syncs',
		description: '',
		hidden: options.isExtensionReadOnly || !supportsPushOrPullMetadata,
	};
	const schedule = {
		name: 'schedule',
		hidden: options.isExtensionReadOnly || !supportsPushOrPullMetadata,
	};
	const project = {
		name: 'project',
		description:
			'Select the LookML Projects to sync Looker and external data lineage',
		hidden: options.isExtensionReadOnly,
	};
	const groups = {
		name: isDatabaseOrWarehouse ? 'schemas' : 'groups',
		description:
			'The resources associated with the selected groups, schemas, and categories will be imported',
		hidden: options.isExtensionReadOnly,
	};
	const pullRequests = {
		name: 'Pull requests',
		description: 'The pull requests associated with the selected integration',
	};

	const importPreferences: PreferenceOption[] = [];

	// Description sync preferences for all the integrations
	if (spec.type === 'builtin') {
		importPreferences.push(PREFERENCE_FORCE_SOURCE_DESCRIPTION);
		if (spec.value.supportsPushMetadata) {
			importPreferences.push(PREFERENCE_PUSH_DESCRIPTION_TO_SOURCE);
		}
	}

	const nonImportPreferences: PreferenceOption[] = [PREFERENCE_AUTO_STALE];

	const preferences: IntegrationTab = {
		name: 'preferences',
		options: [],
		hidden:
			!options.secodaCoreAccess ||
			options.isExtensionReadOnly ||
			!supportsPushOrPullMetadata,
	};
	const mapping = {
		name: 'mapping',
		description: 'The integrations associated with the datasources',
		hidden: options.isExtensionReadOnly,
	};
	const importTab = {
		name: 'import',
		hidden: options.isExtensionReadOnly,
	};

	const notificationPrefrencesTab = {
		name: 'notifications',
		description:
			'The notification preferences associated with the selected integration',
		hidden: options.isExtensionReadOnly,
	};

	const channelsTab = {
		name: 'channels',
		description: 'The channels associated with the selected integration',
		hidden: options.isExtensionReadOnly,
	};

	const alertConfigTab = {
		name: 'alerts',
		description:
			'Configure how Secoda should use PagerDuty to create incidents',
		hidden: options.isExtensionReadOnly,
	};

	const aiTab = {
		name: 'AI',
		title: 'Secoda AI',
		displayName: 'Secoda AI',
		description: 'Control how your AI Slack bot behaves',
		preserveCase: true,
		hidden: options.isExtensionReadOnly,
	};

	const slackSearchTab = {
		name: 'slack-search',
		title: 'Preferences',
		displayName: 'Preferences',
		// description: 'Control what your Slack bot searches for',
		preserveCase: true,
		hidden: options.isExtensionReadOnly,
	};

	if (spec.type === 'marketplace') {
		return [syncs, groups, schedule, connection];
	}

	const builtinSpec = spec.value;

	if (builtinSpec.type === 'ms_teams') {
		return [connection, notificationPrefrencesTab];
	}

	if (builtinSpec.type === 'slack') {
		return [channelsTab, slackSearchTab];
	}

	if (builtinSpec.type === 'pagerduty') {
		return [connection, alertConfigTab];
	}

	if (builtinSpec.type === 'servicenow') {
		return [connection];
	}

	if (builtinSpec.type === 'jira') {
		return [connection, syncs, schedule];
	}

	if (builtinSpec.type === 'github') {
		preferences.options = [
			PREFERENCE_PUSH_SYNC_DBT_COLUMNS,
			PREFERENCE_PUSH_SYNC_NEW_DBT_COLUMNS_WITH_DESCRIPTIONS,
			PREFERENCE_PUSH_SYNC_NEW_DBT_COLUMNS_WITHOUT_DESCRIPTIONS,
		];
		// GitHub is a push integration, we should show syncs, pull requests, and preferences
		return [syncs, pullRequests, preferences, connection];
	}

	if (builtinSpec.type === 'ai_git') {
		// For AI Git integrations with disablePullMetadata, only show connection tab
		if (!supportsPushOrPullMetadata) {
			return [connection];
		}
		return [syncs, connection];
	}

	if (builtinSpec.type === 'glean') {
		// Glean is a push integration, we should show syncs, connections, and preferences
		return [syncs, schedule, connection, preferences];
	}

	if (builtinSpec.type === 'gdrive') {
		return [syncs, connection];
	}

	if (builtinSpec.type === 'linear') {
		return [syncs, connection];
	}

	if (builtinSpec.type === 'looker') {
		tabs = [project];
	} else if (
		builtinSpec.type === 'great_expectations' ||
		builtinSpec.type === 'gx_core'
	) {
		tabs = [mapping];
	}

	if (['metabase', 'looker', 'tableau'].includes(builtinSpec.type)) {
		nonImportPreferences.push(PREFERENCE_EXCLUDE_PERSONAL_COLLECTIONS);
	}

	// There is no backend-end reference to this credential field... leaving this commented out for now
	// in case this is a bug and we need to re-enable it
	// if (['databricks'].includes(builtinSpec.type)) {
	// 	nonImportPreferences.push(PREFERENCE_DISABLE_AUTO_EXTRACT_NEW_SCHEMAS);
	// }

	if (['tableau'].includes(builtinSpec.type)) {
		nonImportPreferences.push(PREFERENCE_ENABLE_EXTRACT_UNPUBLISHED_RESOURCES);
	}

	if (['mssql'].includes(builtinSpec.type)) {
		nonImportPreferences.push(PREFERENCE_DISABLE_SYNC_ALL_DATABASES);
	}

	if (['snowflake'].includes(builtinSpec.type)) {
		nonImportPreferences.push(PREFERENCE_DISABLE_USAGE_EXTRACTION);
	}

	if (['dbt', 'dbt_core'].includes(builtinSpec.type)) {
		// Get rid of groups tab
		tabs = tabs.filter((tab) => tab.name !== 'groups');
		nonImportPreferences.push(PREFERENCE_RESTRICT_TESTS_BY_TEAMS);
	}

	if (['airflow'].includes(builtinSpec.type)) {
		nonImportPreferences.push(PREFERENCE_RESTRICT_TESTS_BY_TEAMS);
	}

	if (['dataform'].includes(builtinSpec.type)) {
		nonImportPreferences.push(PREFERENCE_DISABLE_SYNC_ALL_DATABASES);
	}

	if (['bigquery'].includes(builtinSpec.type)) {
		nonImportPreferences.push(PREFERENCE_ENABLE_FETCHING_JOBS_FROM_30_DAYS);
	}

	if (['glue'].includes(builtinSpec.type)) {
		groups.name = 'schemas';
		tabs.push(groups);
	}

	if (['airbyte'].includes(builtinSpec.type)) {
		tabs.push(groups);
	}

	// Tags import preferences for all the integrations extracting tags information from source
	if (
		[
			'bigquery',
			'confluent cloud',
			'dagster',
			'dataplex',
			'dbt',
			'dbt_core',
			'jira',
			'looker',
			'powerbi',
			'preset',
			'sigma',
			'snowflake',
			'superset',
		].includes(builtinSpec.type)
	) {
		importPreferences.push(PREFERENCE_FORCE_SOURCE_TAGS);
		if (spec.value.supportsPushMetadata) {
			importPreferences.push(PREFERENCE_PUSH_TAGS_TO_SOURCE);
		}
	}

	// Owners import preferences for all integrations extracting owners information from source
	if (
		[
			'cyera',
			'cluvio',
			'airflow',
			'confluence',
			'databricks',
			'dbt',
			'dbt_core',
			'fivetran',
			'jira',
			'confluent_cloud',
			'looker_studio',
			'metabase',
			'mixpanel',
			'mode',
			'omni',
			'powerbi',
			'preset',
			'redash',
			'sigma',
			'superset',
			'tableau',
			'salesforce',
		].includes(builtinSpec.type)
	) {
		importPreferences.push(PREFERENCE_FORCE_SOURCE_OWNERS);
		if (spec.value.supportsPushMetadata) {
			importPreferences.push(PREFERENCE_PUSH_OWNERS_TO_SOURCE);
		}
	}

	if (isSchemaFirstIntegration(builtinSpec)) {
		importPreferences.push(PREFERENCE_DISABLE_NEW_SCHEMA_TO_VISIBLE);
	} else if (isDashboardIntegration(builtinSpec)) {
		importPreferences.push(PREFERENCE_DISABLE_NEW_GROUPS_TO_VISIBLE);
	}

	if (builtinSpec.type === 'confluence') {
		importPreferences.push(PREFERENCE_CONFLUENCE_IMPORT_KNOWLEDGE_BASES);
	}

	preferences.options = [...nonImportPreferences, ...importPreferences];

	if (builtinSpec.type === 'custom') {
		return [importTab, syncs, preferences, connection];
	}

	// For integrations with disablePullMetadata, only show connection tab for the default case
	if (!supportsPushOrPullMetadata) {
		return [connection];
	}

	const addGroupsTab = () => {
		if (
			isSchemaFirstIntegration(builtinSpec) ||
			isDashboardIntegration(builtinSpec) ||
			isDBTCloudIntegration(builtinSpec) ||
			isDataformIntegration(builtinSpec)
		) {
			tabs.push(groups);
		}
	};

	tabs.unshift(syncs, schedule);
	addGroupsTab();
	tabs.push(preferences, connection);

	return tabs;
}

export const integrationPreferences = {
	clickhouse: {
		sync_all_databases: {
			title: 'Sync All Databases',
			description: 'Sync all databases in the ClickHouse instance',
			type: 'boolean',
			default: true,
		},
		disable_sync_all_databases: {
			title: 'Disable Sync All Databases',
			description: 'Only sync the specified database',
			type: 'boolean',
			default: false,
		},
	},
} as const;
